import { useCallback, useMemo } from "react";

import { usePatientDischargeSummaries } from "features/documents/hooks/usePatientDischargeSummaries";
import { usePatientDeaspCertificates } from "features/healthlink/hooks/usePatientDeaspCertificates";

import { mergeAllDocumentRows } from "../utils/allDocumentsRows";

/*
 * Everything "All Documents" has to list, from the three endpoints that hold it (KIN-2068).
 *
 * Only All merges: every other section is either a document type (served by the documents
 * endpoint) or its own section with its own table, so the two extra queries are disabled
 * elsewhere rather than fired for a list that will never show them.
 */

/*
 * All pages client-side over the merged array, so each foreign source is asked for one generous
 * page instead of being paged in step with the others. 100 is the same page size the email
 * modal's certificate picker already requests, so that DEASP page is frequently a cache hit.
 * A patient with more than 100 of either sees the newest 100 in All and the full list, paged, in
 * that source's own section.
 */
const MERGED_SOURCE_ROW_LIMIT = 100;

const DISCHARGE_SUMMARIES_ERROR_LABEL = "this patient's discharge summaries";
const DEASP_ERROR_LABEL = "this patient's sick certificates";

export const useAllDocumentsSources = ({
  patientId,
  uploads = [],
  isActive = false,
  isUploadsFetching = false,
}) => {
  const {
    summaries,
    isLoading: isLoadingDischargeSummaries,
    isError: hasDischargeSummariesError,
    refetch: refetchDischargeSummaries,
  } = usePatientDischargeSummaries({
    patientId,
    page: 1,
    rows: MERGED_SOURCE_ROW_LIMIT,
    enabled: isActive,
  });

  const {
    data: deaspResponse,
    isLoading: isLoadingDeaspCertificates,
    isError: hasDeaspError,
    refetch: refetchDeaspCertificates,
  } = usePatientDeaspCertificates({
    patientId,
    page: 1,
    rows: MERGED_SOURCE_ROW_LIMIT,
    enabled: isActive,
  });

  const rows = useMemo(() => {
    if (!isActive) return uploads;

    return mergeAllDocumentRows({
      uploads,
      dischargeSummaries: summaries,
      deaspCertificates: deaspResponse?.data ?? [],
    });
  }, [isActive, uploads, summaries, deaspResponse]);

  /*
   * A source that failed must not read as a source that is empty — that is the defect this ticket
   * exists to fix, and it would be reintroduced silently by a swallowed error.
   */
  const failedSourceLabels = [
    hasDischargeSummariesError && DISCHARGE_SUMMARIES_ERROR_LABEL,
    hasDeaspError && DEASP_ERROR_LABEL,
  ].filter(Boolean);

  const retryFailedSources = useCallback(() => {
    if (hasDischargeSummariesError) refetchDischargeSummaries?.();
    if (hasDeaspError) refetchDeaspCertificates?.();
  }, [
    hasDischargeSummariesError,
    hasDeaspError,
    refetchDischargeSummaries,
    refetchDeaspCertificates,
  ]);

  /*
   * Uploaded documents must still appear the moment they arrive — All cannot start waiting on two
   * extra requests before it shows anything. So the table only reports "loading" while it has
   * nothing at all to show; once there is a row, the slower sources merge in behind it.
   */
  const isLoading =
    isUploadsFetching ||
    (rows.length === 0 &&
      (isLoadingDischargeSummaries || isLoadingDeaspCertificates));

  return {
    rows,
    isLoading,
    failedSourceLabels,
    retryFailedSources,
  };
};
